import React, { useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { studioImportApi } from './adminImportApi';
import { clearDraft, loadDraft } from './importDrafts';
import { buildSheet, normalizeHeader, readWorkbook } from './productImport';
import { recordHeaderSet } from './recordImport';
import { CATALOG_SHEET_NAMES, IMPORT_ORDER, RECORD_TYPES } from './recordTypes';
import useStudioCatalog from './useStudioCatalog';
import { ImportHeader, ImportStepper, ImportTarget } from './ImportFrame';
import ImportUploadStep from './ImportUploadStep';
import ProductImportWizard from './ProductImportWizard';
import RecordImportWizard from './RecordImportWizard';
import '../../../styles/ProductImport.css';

export function importNouns(destination, itemType) {
  if (destination === 'catalog') {
    return itemType === 'service'
      ? { noun: 'service', nouns: 'services', Nouns: 'Services' }
      : { noun: 'product', nouns: 'products', Nouns: 'Products' };
  }
  const { noun, nouns, label } = RECORD_TYPES[destination];
  return { noun, nouns, Nouns: label };
}

// Which import a sheet holds, judged by its name: Ivy's sections ("Clients",
// "Time Tracks") and the names people give sheets themselves.
export function destinationForSheet(name) {
  const key = normalizeHeader(name);
  for (const [itemType, names] of Object.entries(CATALOG_SHEET_NAMES)) {
    if (names.includes(key)) return { destination: 'catalog', itemType };
  }
  for (const [type, def] of Object.entries(RECORD_TYPES)) {
    if (def.sheetNames.includes(key)) return { destination: type, itemType: 'product' };
  }
  return null;
}

const sameStep = (a, b) =>
  a.destination === b.destination && (a.destination !== 'catalog' || a.itemType === b.itemType);

// Picks the studio's file and what it holds, then hands over to the product or
// the record import for mapping, review and saving.
export default function DataImportWizard({ account, onChangeAccount }) {
  const studioId = account.userId;
  const api = useMemo(() => studioImportApi(studioId), [studioId]);

  const [destination, setDestination] = useState('catalog');
  const [itemType, setItemType] = useState('product');
  // Whether the admin picked the destination, rather than it being the default.
  const [destinationChosen, setDestinationChosen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [reading, setReading] = useState(false);
  const [parseError, setParseError] = useState('');
  const [sheets, setSheets] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [flow, setFlow] = useState(null);
  const [savedDraft, setSavedDraft] = useState(() => loadDraft(studioId));
  // Sheet name -> how many rows its last import saved, for the section list.
  const [finished, setFinished] = useState({});

  const catalog = useStudioCatalog(api, itemType);
  const isRecords = destination !== 'catalog';
  const { nouns, Nouns } = importNouns(destination, itemType);
  const sheet = sheets.find((s) => s.name === sheetName) || null;

  // The chosen sheet read for the chosen import: records keep one-cell rows and
  // score the header row against their own column names.
  const built = useMemo(() => {
    if (!sheet) return null;
    const options = isRecords
      ? { knownHeaders: recordHeaderSet(destination), keepSingleCellRows: true }
      : {};
    return {
      ...buildSheet(sheet.name, sheet.rows, {
        ...options,
        rowNumbers: sheet.rowNumbers,
        extras: sheet.extras,
      }),
      date1904: Boolean(sheet.date1904),
      section: Boolean(sheet.section),
    };
  }, [sheet, isRecords, destination]);
  const emptySheet = built && !built.dataRows.length;
  const sheetError = emptySheet
    ? `"${built.name}" has column names but no ${nouns} under them.`
    : '';

  const isIvy = sheets.some((s) => s.section);

  /* ----- File ----- */
  const resetFile = () => {
    setFileName('');
    setParseError('');
    setSheets([]);
    setSheetName('');
  };

  const choose = ({ destination: next, itemType: nextType }) => {
    setDestination(next);
    if (next === 'catalog' && nextType) setItemType(nextType);
  };

  // The first step of the suggested order the file has and hasn't done yet.
  const nextStepIn = (found, done) =>
    IMPORT_ORDER.map((step) => ({
      step,
      sheet: found.find((s) => {
        const match = destinationForSheet(s.name);
        return match && sameStep(match, step);
      }),
    })).find(({ sheet: s }) => s && done[s.name] === undefined);

  const handleFile = async (file) => {
    resetFile();
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setFileName(file.name);
      setParseError('Choose an Excel (.xlsx) or CSV file.');
      return;
    }
    setReading(true);
    setFileName(file.name);
    try {
      const csv = /\.csv$/i.test(file.name);
      const data = csv ? await file.text() : new Uint8Array(await file.arrayBuffer());
      const found = readWorkbook(data, { csv });
      if (!found.length) throw new Error('This file is empty.');
      setSheets(found);
      setFinished({});
      // A sheet named for an import the admin picked wins; otherwise an account
      // export starts at the top of the suggested order.
      const forChoice = found.find((s) => {
        const match = destinationForSheet(s.name);
        return match && sameStep(match, { destination, itemType });
      });
      const keepChoice = forChoice && (destinationChosen || !found.some((s) => s.section));
      const next = !keepChoice && found.some((s) => s.section) ? nextStepIn(found, {}) : null;
      if (next) {
        choose(next.step);
        setSheetName(next.sheet.name);
      } else {
        setSheetName((forChoice || found[0]).name);
      }
    } catch (err) {
      console.error('Failed to read import file', err);
      setParseError(
        err?.message || "We couldn't read this file. Save it as .xlsx or .csv and try again."
      );
    } finally {
      setReading(false);
    }
  };

  const handleSheetChange = (name) => {
    setSheetName(name);
    const match = destinationForSheet(name);
    if (match) choose(match);
  };

  const sheetFor = (choice) =>
    sheets.find((s) => {
      const match = destinationForSheet(s.name);
      return match && sameStep(match, choice);
    });

  const handleDestinationChange = (next) => {
    setDestination(next);
    setDestinationChosen(true);
    const match = sheetFor({ destination: next, itemType });
    if (match) setSheetName(match.name);
  };

  const handleItemTypeChange = (type) => {
    setItemType(type);
    setDestinationChosen(true);
    const match = sheetFor({ destination: 'catalog', itemType: type });
    if (match) setSheetName(match.name);
  };

  const pickStep = (step, name) => {
    choose(step);
    setSheetName(name);
  };

  /* ----- Drafts (product imports only) ----- */
  const resumeDraft = () => {
    if (!savedDraft) return;
    setItemType(savedDraft.itemType || 'product');
    setDestination('catalog');
    setFlow({ kind: 'catalog', key: Date.now(), draft: savedDraft });
  };

  const discardDraft = () => {
    clearDraft(studioId);
    setSavedDraft(null);
  };

  /* ----- Moving between the upload step and an import ----- */
  const start = () => {
    if (!built || emptySheet) return;
    // Vendors imported a moment ago should show as the studio's, not as new.
    if (!isRecords) catalog.reload();
    setFlow({ kind: isRecords ? 'records' : 'catalog', key: Date.now(), draft: null });
  };

  const backToUpload = () => {
    setSavedDraft(loadDraft(studioId));
    setFlow(null);
  };

  const importAnotherFile = () => {
    resetFile();
    backToUpload();
  };

  // Ivy's proposals are read from Invoices and Invoice Items whichever was
  // picked, so they count against the section the suggested order lists.
  const finishedName = flow?.draft
    ? null
    : (isIvy && destination === 'proposals' && sheetFor({ destination })?.name) || sheetName;
  const recordFinished = (count) => {
    if (finishedName) setFinished((prev) => ({ ...prev, [finishedName]: count }));
  };

  // Another sheet of the same file: the next step of the suggested order for an
  // account export, otherwise back to the sheet list.
  const importNextSheet = () => {
    const done = finishedName ? { ...finished, [finishedName]: true } : finished;
    const next = isIvy ? nextStepIn(sheets, done) : null;
    if (next) pickStep(next.step, next.sheet.name);
    backToUpload();
  };
  const nextSheetLabel =
    sheets.length > 1 && !flow?.draft
      ? `Import another ${isIvy ? 'section' : 'sheet'} from this file`
      : '';

  if (!catalog.loaded) {
    return (
      <div className="pi-page">
        <ImportTarget account={account} onChangeStudio={onChangeAccount} />
        {catalog.loadError ? (
          <div className="pi-banner pi-banner--error" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            <span>{catalog.loadError}</span>
            <div className="pi-banner-actions">
              <button type="button" className="pi-btn pi-btn--outline" onClick={catalog.reload}>
                Try again
              </button>
            </div>
          </div>
        ) : (
          <p className="pi-card pi-empty">
            <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Loading the studio's
            catalog…
          </p>
        )}
      </div>
    );
  }

  const shared = {
    account,
    onChangeAccount,
    fileName: flow?.draft ? flow.draft.fileName || '' : fileName,
    onBackToUpload: backToUpload,
    onImportAnother: importAnotherFile,
    onImportNextSheet: nextSheetLabel ? importNextSheet : null,
    nextSheetLabel,
    onFinished: recordFinished,
  };

  if (flow?.kind === 'catalog') {
    return (
      <ProductImportWizard
        key={flow.key}
        {...shared}
        catalog={catalog}
        itemType={itemType}
        sheet={flow.draft ? null : built}
        draft={flow.draft}
      />
    );
  }

  if (flow?.kind === 'records') {
    return (
      <RecordImportWizard
        key={flow.key}
        {...shared}
        type={destination}
        sheet={built}
        workbook={sheets}
        source={isIvy ? 'ivy' : ''}
      />
    );
  }

  return (
    <div className="pi-page">
      <ImportTarget account={account} onChangeStudio={onChangeAccount} />
      <ImportHeader
        title={`Import ${nouns}`}
        subtitle={
          isRecords
            ? `Add ${nouns} to this studio from an Excel or CSV export.`
            : `Add ${nouns} to this studio's catalog from an Excel export.`
        }
      />
      <ImportStepper
        labels={['Upload file', 'Map Columns', `Review ${Nouns}`, 'Import']}
        step={1}
      />
      <div className="pi-body pi-body--step1">
        <ImportUploadStep
          destination={destination}
          onDestinationChange={handleDestinationChange}
          itemType={itemType}
          onItemTypeChange={handleItemTypeChange}
          fileName={fileName}
          reading={reading}
          parseError={parseError || sheetError}
          onFile={handleFile}
          onClearFile={resetFile}
          sheets={sheets}
          sheetName={sheetName}
          onSheetChange={handleSheetChange}
          destinationForSheet={destinationForSheet}
          finished={finished}
          onPickStep={pickStep}
          draft={savedDraft}
          onResumeDraft={resumeDraft}
          onDiscardDraft={discardDraft}
          onCancel={onChangeAccount}
          onContinue={start}
          canContinue={Boolean(built) && !emptySheet && !reading && !parseError}
        />
      </div>
    </div>
  );
}
